import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'
import Papa from 'papaparse'

/**
 * Get today's date in YYYY-MM-DD format
 */
const getTodayDate = () => {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

/**
 * Check if user has attendance record for today
 * @param {string} employeeId - User's employee ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const checkTodayAttendance = async (employeeId) => {
  try {
    const todayDate = getTodayDate()
    const attendanceRef = doc(db, 'attendance', `${employeeId}_${todayDate}`)
    const attendanceDoc = await getDoc(attendanceRef)

    if (attendanceDoc.exists()) {
      const data = attendanceDoc.data()
      return {
        success: true,
        data: {
          ...data,
          isCheckedIn: true,
          hasCheckedOut: !!data.checkOut
        }
      }
    } else {
      return {
        success: true,
        data: { isCheckedIn: false }
      }
    }
  } catch (error) {
    console.error('Check today attendance error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Clock in - Create new attendance record for today
 * @param {string} employeeId - User's employee ID
 * @param {string} uid - User's Firebase Auth UID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const checkIn = async (employeeId, uid) => {
  try {
    const todayDate = getTodayDate()
    const now = Timestamp.now()

    const attendanceData = {
      employeeId,
      uid,
      date: todayDate,
      checkIn: now,
      checkOut: null,
      status: 'active',
      createdAt: now.toDate().toISOString(),
      updatedAt: now.toDate().toISOString()
    }

    // Use composite key: employeeId_date
    const attendanceRef = doc(db, 'attendance', `${employeeId}_${todayDate}`)
    await setDoc(attendanceRef, attendanceData)

    return {
      success: true,
      data: {
        ...attendanceData,
        isCheckedIn: true,
        hasCheckedOut: false
      }
    }
  } catch (error) {
    console.error('Check in error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Clock out - Update existing attendance record with checkOut time
 * @param {string} employeeId - User's employee ID
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const checkOut = async (employeeId) => {
  try {
    const todayDate = getTodayDate()
    const now = Timestamp.now()

    const attendanceRef = doc(db, 'attendance', `${employeeId}_${todayDate}`)

    // Check if document exists
    const attendanceDoc = await getDoc(attendanceRef)
    if (!attendanceDoc.exists()) {
      return {
        success: false,
        error: 'No check-in record found for today. Please check in first.'
      }
    }

    // Update with checkOut time
    await updateDoc(attendanceRef, {
      checkOut: now,
      status: 'completed',
      updatedAt: now.toDate().toISOString()
    })

    const updatedDoc = await getDoc(attendanceRef)

    return {
      success: true,
      data: {
        ...updatedDoc.data(),
        isCheckedIn: true,
        hasCheckedOut: true
      }
    }
  } catch (error) {
    console.error('Check out error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get attendance history for a user
 * @param {string} employeeId - User's employee ID
 * @param {number} limit - Number of records to fetch (default: 30)
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const getAttendanceHistory = async (employeeId, limit = 30) => {
  try {
    const attendanceQuery = query(
      collection(db, 'attendance'),
      where('employeeId', '==', employeeId),
      orderBy('date', 'desc'),
      firestoreLimit(limit)
    )

    const querySnapshot = await getDocs(attendanceQuery)
    const attendanceRecords = []

    querySnapshot.forEach((doc) => {
      attendanceRecords.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return {
      success: true,
      data: attendanceRecords
    }
  } catch (error) {
    console.error('Get attendance history error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Format timestamp to readable time string
 * @param {Timestamp} timestamp - Firebase Timestamp
 * @returns {string} - Formatted time (e.g., "9:30 AM")
 */
export const formatCheckInTime = (timestamp) => {
  if (!timestamp) return ''

  const date = timestamp.toDate()
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Calculate hours worked from check-in and check-out times
 * @param {Timestamp} checkIn - Check-in timestamp
 * @param {Timestamp} checkOut - Check-out timestamp
 * @returns {string} - Hours worked (e.g., "8.5h")
 */
export const calculateHoursWorked = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return '0h'

  const checkInDate = checkIn.toDate()
  const checkOutDate = checkOut.toDate()
  const diffMs = checkOutDate - checkInDate
  const diffHours = (diffMs / (1000 * 60 * 60)).toFixed(1)

  return `${diffHours}h`
}

/**
 * Get total worked hours for a specific date
 * @param {string} employeeId - Employee ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<{ success: boolean, hours?: number, hasCheckOut?: boolean, error?: string }>}
 */
export const getTotalWorkedHours = async (employeeId, date) => {
  try {
    const attendanceRef = doc(db, 'attendance', `${employeeId}_${date}`)
    const attendanceDoc = await getDoc(attendanceRef)

    if (!attendanceDoc.exists()) {
      return { success: true, hours: 0, hasCheckOut: false }
    }

    const data = attendanceDoc.data()

    if (!data.checkOut) {
      return { success: true, hours: 0, hasCheckOut: false }
    }

    const checkInDate = data.checkIn.toDate()
    const checkOutDate = data.checkOut.toDate()
    const diffMs = checkOutDate - checkInDate
    const hours = parseFloat((diffMs / (1000 * 60 * 60)).toFixed(2))

    return { success: true, hours, hasCheckOut: true }
  } catch (error) {
    console.error('Get total worked hours error:', error)
    return { success: false, error: error.message, hours: 0, hasCheckOut: false }
  }
}

/**
 * Calculate numeric hours from timestamps (helper for timesheet)
 * @param {Timestamp} checkIn - Firebase Timestamp
 * @param {Timestamp} checkOut - Firebase Timestamp
 * @returns {number} - Hours as decimal (e.g., 8.5)
 */
export const calculateNumericHours = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0
  const checkInDate = checkIn.toDate()
  const checkOutDate = checkOut.toDate()
  const diffMs = checkOutDate - checkInDate
  return parseFloat((diffMs / (1000 * 60 * 60)).toFixed(2))
}

/**
 * Internal helper: Enrich attendance records with employee names using BATCHED queries
 * Uses Firebase IN operator to minimize queries (30 IDs per batch max)
 * PERFORMANCE CRITICAL: 25-30x faster than individual queries
 * @param {array} attendanceRecords - Attendance records to enrich
 * @returns {Promise<array>} - Records with employeeName added
 */
const enrichWithEmployeeNames = async (attendanceRecords) => {
  if (!attendanceRecords || attendanceRecords.length === 0) {
    return []
  }

  // Extract unique employeeIds
  const uniqueEmployeeIds = [...new Set(attendanceRecords.map(r => r.employeeId))]

  // Split into chunks of 30 (Firebase IN operator limit)
  const chunkSize = 30
  const chunks = []
  for (let i = 0; i < uniqueEmployeeIds.length; i += chunkSize) {
    chunks.push(uniqueEmployeeIds.slice(i, i + chunkSize))
  }

  // Execute all batches in PARALLEL using Promise.all
  const batchPromises = chunks.map(async (chunk) => {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('employeeId', 'in', chunk)
      )
      const snapshot = await getDocs(usersQuery)

      const mapping = {}
      snapshot.forEach(doc => {
        const data = doc.data()
        mapping[data.employeeId] = data.fullName
      })
      return mapping
    } catch (error) {
      if (error.code === 'permission-denied') {
        return {}
      }
      throw error
    }
  })

  let batchResults = []
  try {
    batchResults = await Promise.all(batchPromises)
  } catch (error) {
    if (error.code !== 'permission-denied') {
      throw error
    }
  }

  // Merge all mappings into single object
  const usersMap = Object.assign({}, ...batchResults)

  // Enrich records with employee names
  return attendanceRecords.map(record => ({
    ...record,
    employeeName: usersMap[record.employeeId]
      || record.employeeName
      || record.employeeId
  }))
}

/**
 * Get all attendance records (super_admin only)
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const getAllAttendanceRecords = async () => {
  try {
    const attendanceQuery = query(
      collection(db, 'attendance'),
      orderBy('date', 'desc')
    )

    const querySnapshot = await getDocs(attendanceQuery)
    const records = []

    querySnapshot.forEach((doc) => {
      records.push({
        id: doc.id,
        ...doc.data()
      })
    })

    // Enrich with employee names using batched queries
    const enrichedRecords = await enrichWithEmployeeNames(records)

    return { success: true, data: enrichedRecords }
  } catch (error) {
    console.error('Get all attendance records error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get attendance records filtered by date range and optional employee
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} employeeId - Optional employee ID filter
 * @returns {Promise<{success: boolean, data?: array, error?: string}>}
 */
export const getAttendanceByDateRange = async (startDate, endDate, employeeId = null) => {
  try {
    let attendanceQuery

    if (employeeId) {
      // Filter by employee and date range
      attendanceQuery = query(
        collection(db, 'attendance'),
        where('employeeId', '==', employeeId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      )
    } else {
      // Filter by date range only
      attendanceQuery = query(
        collection(db, 'attendance'),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      )
    }

    const querySnapshot = await getDocs(attendanceQuery)
    const records = []

    querySnapshot.forEach((doc) => {
      records.push({
        id: doc.id,
        ...doc.data()
      })
    })

    // Enrich with employee names using batched queries
    const enrichedRecords = await enrichWithEmployeeNames(records)

    return { success: true, data: enrichedRecords }
  } catch (error) {
    console.error('Get attendance by date range error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Export attendance records to CSV file
 * @param {array} records - Attendance records to export
 * @returns {object} - {success: boolean, count?: number, error?: string}
 */
export const exportAttendanceToCSV = (records) => {
  try {
    // Validate input
    if (!records || !Array.isArray(records)) {
      return { success: false, error: 'Invalid records data' }
    }

    if (records.length === 0) {
      return { success: false, error: 'No records to export' }
    }

    // Map records to CSV format
    const csvData = records.map(record => {
      const checkInFormatted = record.checkIn ? formatCheckInTime(record.checkIn) : 'N/A'
      const checkOutFormatted = record.checkOut ? formatCheckInTime(record.checkOut) : 'Still working'
      const totalHours = record.checkOut
        ? calculateNumericHours(record.checkIn, record.checkOut).toFixed(2)
        : '0.00'
      const status = record.checkOut ? 'Complete' : 'In Progress'

      return {
        'Employee Name': record.employeeName || 'Unknown',
        'Employee ID': record.employeeId || 'N/A',
        'Date': record.date || 'N/A',
        'Check In': checkInFormatted,
        'Check Out': checkOutFormatted,
        'Total Hours': totalHours,
        'Status': status
      }
    })

    // Convert to CSV using papaparse
    const csv = Papa.unparse(csvData, {
      quotes: true,
      header: true
    })

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      const today = getTodayDate()
      const filename = `attendance_${today}.csv`

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setTimeout(() => URL.revokeObjectURL(url), 100)

      return { success: true, count: records.length }
    } else {
      return { success: false, error: 'Browser does not support file download' }
    }
  } catch (error) {
    console.error('Export to CSV error:', error)
    return { success: false, error: error.message || 'Failed to export CSV' }
  }
}
